import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { useHubLogin } from '../../session';
import type { Tenant } from '../../identity/api';
import {
  authClientFor,
  identityPath,
  loadTargets,
  scimFor,
  targetLoginError,
  targetSession,
  useTargets,
  type AuthTargetInfo,
  type ScimEndpoint,
} from '../../identity/session';
import { joinParts, type Issue } from './shared';

// Identity: per AUTH environment, what AUTH and its SCIM gateway hold, and what looks wrong.

export interface TargetSummary {
  target: AuthTargetInfo;
  signedIn: boolean;
  /** Why the environment could not be read (sign-in failed, AUTH refused, unreachable). */
  error: string | null;
  customers: number | null;
  tenants: {
    total: number;
    active: number;
    suspended: number;
    deleted: number;
    database: number;
    workserver: number;
    /** Workserver tenants without an identity provider URL: sign-in cannot work. */
    noProvider: Tenant[];
  } | null;
  scim: {
    total: number;
    auth: number;
    nycelogic: number;
    traced: ScimEndpoint[];
    /** `auth` rows whose tenant is not an active Database tenant of this AUTH. */
    orphans: ScimEndpoint[];
    /** `auth` rows of a tenant here that point somewhere else than this AUTH's provisioning API. */
    misrouted: ScimEndpoint[];
    connected: number;
  } | null;
  scimError: string | null;
}

const PAGE = 100;

async function allTenants(id: string): Promise<Tenant[]> {
  const client = authClientFor(id);
  const first = await client.tenants.list({ page: 1, pageSize: PAGE });
  const rest = await Promise.all(
    Array.from({ length: Math.min(20, Math.ceil(first.total / PAGE) - 1) }, (_, i) =>
      client.tenants.list({ page: i + 2, pageSize: PAGE })
    )
  );
  return [first, ...rest].flatMap((p) => p.items);
}

const expectedEndpoint = (authUrl: string, tenantId: string) =>
  `${authUrl.replace(/\/+$/, '')}/api/auth/v1/tenants/${tenantId}/provisioning/`;

async function summarize(t: AuthTargetInfo, canAuth: boolean, canScim: boolean): Promise<TargetSummary> {
  const base: TargetSummary = {
    target: t,
    signedIn: targetSession(t.id).isSignedIn(),
    error: null,
    customers: null,
    tenants: null,
    scim: null,
    scimError: null,
  };
  if (!base.signedIn) return { ...base, error: targetLoginError(t.id) ?? 'not signed in' };
  let tenants: Tenant[] = [];
  if (canAuth) {
    try {
      const [list, customers] = await Promise.all([
        allTenants(t.id),
        authClientFor(t.id).customers.list({ page: 1, pageSize: 1 }),
      ]);
      tenants = list;
      const live = list.filter((x) => x.status !== 'Deleted');
      base.customers = customers.total;
      base.tenants = {
        total: live.length,
        active: list.filter((x) => x.status === 'Active').length,
        suspended: list.filter((x) => x.status === 'Suspended').length,
        deleted: list.filter((x) => x.status === 'Deleted').length,
        database: live.filter((x) => x.identityProvider === 'Database').length,
        workserver: live.filter((x) => x.identityProvider === 'Workserver').length,
        noProvider: live.filter((x) => x.identityProvider === 'Workserver' && !x.identityProviderUrl.trim()),
      };
    } catch (e) {
      base.error = (e as Error).message;
    }
  }
  if (canScim && t.scimUrl) {
    try {
      const rows = await scimFor(t.id).list();
      const dbTenants = new Map(
        tenants
          .filter((x) => x.identityProvider === 'Database' && x.status === 'Active')
          .map((x) => [x.id, x])
      );
      const authRows = rows.filter((r) => r.target === 'auth');
      const known = new Set(tenants.map((x) => x.id));
      base.scim = {
        total: rows.length,
        auth: authRows.length,
        nycelogic: rows.length - authRows.length,
        traced: rows.filter((r) => r.trace),
        // without the tenant list (no AUTH read) nothing can be called an orphan
        orphans: canAuth && base.tenants ? authRows.filter((r) => !dbTenants.has(r.tenantId)) : [],
        misrouted: authRows.filter(
          (r) => known.has(r.tenantId) && r.endpoint !== expectedEndpoint(t.authUrl, r.tenantId)
        ),
        connected: authRows.filter(
          (r) => dbTenants.has(r.tenantId) && r.endpoint === expectedEndpoint(t.authUrl, r.tenantId)
        ).length,
      };
    } catch (e) {
      base.scimError = (e as Error).message;
    }
  }
  return base;
}

/** Everything worth a look, across environments. */
export function identityIssues(list: TargetSummary[]): Issue[] {
  const out: Issue[] = [];
  // A signed-out environment is the operator's choice, not a problem (its card says "signed out"); only when no
  // environment is signed in is the state of Identity unknown.
  const anySignedIn = list.some((s) => s.signedIn);
  for (const s of list) {
    const env = s.target.label;
    const tenantLink = (id: string) =>
      identityPath(`/identity/tenants/${encodeURIComponent(id)}`, s.target.id);
    if (s.error && (s.signedIn || !anySignedIn))
      out.push({
        key: `id:${s.target.id}:error`,
        status: s.signedIn ? 'error' : 'unknown',
        name: env,
        what: 'AUTH environment',
        detail: s.signedIn
          ? s.error
          : `not signed in${s.error && s.error !== 'not signed in' ? `: ${s.error}` : ''}`,
        to: identityPath('/identity/tenants', s.target.id),
      });
    if (s.scimError)
      out.push({
        key: `id:${s.target.id}:scim`,
        status: 'error',
        name: `${env} SCIM gateway`,
        what: 'SCIM',
        detail: s.scimError,
        to: identityPath('/identity/scim', s.target.id),
      });
    for (const t of s.tenants?.noProvider ?? [])
      out.push({
        key: `id:${s.target.id}:np:${t.id}`,
        status: 'down',
        name: t.name,
        what: `tenant · ${env}`,
        detail: 'Workserver tenant without identity provider URL: sign-in fails',
        to: tenantLink(t.id),
      });
    for (const r of s.scim?.orphans ?? [])
      out.push({
        key: `id:${s.target.id}:orphan:${r.tenantId}`,
        status: 'down',
        name: r.tenantId,
        what: `SCIM endpoint · ${env}`,
        detail: 'routes to AUTH, but no active Database tenant has this id: provisioning fails',
        to: identityPath('/identity/scim', s.target.id),
      });
    for (const r of s.scim?.misrouted ?? [])
      out.push({
        key: `id:${s.target.id}:mis:${r.tenantId}`,
        status: 'warn',
        name: r.tenantId,
        what: `SCIM endpoint · ${env}`,
        detail: `points at ${r.endpoint}, not this AUTH's provisioning API`,
        to: identityPath('/identity/scim', s.target.id),
      });
    // one line per environment: trace is often left on for several tenants at once
    const traced = s.scim?.traced ?? [];
    if (traced.length)
      out.push({
        key: `id:${s.target.id}:trace`,
        status: 'warn',
        name: traced.length === 1 ? traced[0].tenantId : `${traced.length} SCIM endpoints`,
        what: `SCIM · ${env}`,
        detail: `trace is on${
          traced.length > 1
            ? ` (${traced
                .map((r) => r.tenantId)
                .slice(0, 3)
                .join(', ')}${traced.length > 3 ? ', …' : ''})`
            : ''
        }: request and response bodies (personal data) are logged`,
        to: identityPath('/identity/scim', s.target.id),
      });
  }
  return out;
}

export const identityHeadline = (list: TargetSummary[]) =>
  joinParts(
    list.map(
      (s) =>
        `${s.target.label}: ${s.tenants ? `${s.tenants.active} active tenants` : s.signedIn ? 'error' : 'signed out'}`
    )
  );

// Shared between the main and the Identity dashboard; AUTH is not ours to hammer, so 2 minutes.
const TTL = 120_000;
let cache: { at: number; key: string; data: Promise<TargetSummary[]> } | null = null;

/** The summaries of every environment (callers with ophub:auth:read or ophub:scim:read), cached for 2 min. */
export function useIdentitySummary() {
  const hub = useHubLogin();
  const canAuth = hasScope(hub?.scopes, SCOPES.authRead);
  const canScim = hasScope(hub?.scopes, SCOPES.scimRead);
  const enabled = canAuth || canScim;
  const { signedIn, targets } = useTargets();
  const signature = `${canAuth}${canScim}|${targets.map((t) => `${t.id}:${signedIn(t.id)}`).join(',')}`;
  const [state, setState] = useState<{
    data: TargetSummary[] | null;
    loading: boolean;
    error: string | null;
  }>({
    data: null,
    loading: enabled,
    error: null,
  });
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    const fresh = round > 0 || !cache || cache.key !== signature || Date.now() - cache.at > TTL;
    if (fresh) {
      cache = {
        at: Date.now(),
        key: signature,
        data: loadTargets().then((list) => Promise.all(list.map((t) => summarize(t, canAuth, canScim)))),
      };
    }
    cache!.data.then(
      (data) => alive && setState({ data, loading: false, error: null }),
      (e) => alive && setState({ data: null, loading: false, error: (e as Error).message })
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, signature, round]);
  return { ...state, enabled, canAuth, canScim, reload: () => setRound((r) => r + 1) };
}

/** Test hook. */
export const resetIdentitySummary = () => {
  cache = null;
};
